import React from "react";
import {
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Checkbox,
    Box,
    Typography,
    useTheme,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon as MuiListItemIcon,
    ListItemText as MuiListItemText
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";

export type DtListActionType = "edit" | "delete" | "view" | "custom";

export interface DtListAction {
    type: DtListActionType;
    label?: string;
    icon?: React.ReactNode;
    onClick?: (item: DtListItem, index: number) => void;
}

interface DtListActionConfig {
    mode?: "inline" | "dropdown";
    actions: DtListAction[];
}

export interface DtListItem {
    id: string | number;
    label: React.ReactNode;
    checked?: boolean;
    disabled?: boolean;
    secondary?: string;
    actionConfig?: DtListActionConfig;
    content?: React.ReactNode;
    children?: DtListItem[];
    Order?: number; 
}

interface DtListProps {
    items: DtListItem[];
    selectable?: boolean;
    onSelect?: (id: string | number, checked: boolean) => void;
    dense?: boolean;
    sx?: object;
    listItemSx?: object | ((item: DtListItem, idx: number) => object); // <-- add this
}

export const getDefaultIcon = (type: DtListActionType) => {
    if (type === "edit") return <EditIcon fontSize="small" color="primary" />;
    if (type === "delete") return <DeleteIcon fontSize="small" color="primary" />;
    if (type === "view") return <VisibilityIcon fontSize="small" />;
    return null;
};

const DtList: React.FC<DtListProps> = ({
    items,
    selectable = false,
    onSelect,
    dense = false,
    sx,
    listItemSx // <-- add this
}) => {
    const theme = useTheme();
    const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
    const [menuItemIdx, setMenuItemIdx] = React.useState<number | null>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, idx: number) => {
        setMenuAnchor(event.currentTarget);
        setMenuItemIdx(idx);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setMenuItemIdx(null);
    };

    const sortByOrder = (items: DtListItem[]): DtListItem[] => {
        return [...items]
            .sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0))
            .map(item => ({
                ...item,
                children: item.children ? sortByOrder(item.children) : undefined
            }));
    };

    const sortedItems = sortByOrder(items);


    return (
        <List sx={{ width: "100%", bgcolor: "background.paper", borderRadius: 2, ...sx }}>
            {sortedItems.map((item, idx) => {
                // Use actionConfig from item
                const actionConfig = item.actionConfig ?? { actions: [] };
                const actions = Array.isArray(actionConfig.actions) ? actionConfig.actions : [];
                const useDropdown =
                    actionConfig.mode === "dropdown" || actions.length > 3;

                const customSx =
                    typeof listItemSx === "function"
                        ? listItemSx(item, idx)
                        : listItemSx || {};
                return (
                    <ListItem
                        key={item.id}
                        dense={dense}
                        disableGutters
                        onClick={() => {
                            if (selectable && onSelect && !item.disabled) {
                                onSelect(item.id, !item.checked);
                            }
                        }}
                        sx={{
                            borderRadius: 2,
                            mb: 0.5,
                            px: 1.5,
                            py: 0.5,
                            color: item.disabled ? theme.palette.text.disabled : theme.palette.text.primary,
                            cursor: item.disabled ? "not-allowed" : "pointer",
                            "&:hover": {
                                background: !item.disabled ? `${theme.palette.info.main}10` : undefined,
                            },
                            opacity: item.disabled ? 0.6 : 1,
                            transition: "background 0.2s",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            ...customSx // <-- merge custom styles
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                            {selectable && (
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <Checkbox
                                        edge="start"
                                        checked={!!item.checked}
                                        disabled={item.disabled}
                                        tabIndex={-1}
                                        onChange={e => onSelect && onSelect(item.id, e.target.checked)}
                                        sx={{
                                            color: theme.palette.primary.main,
                                            '&.Mui-checked': {
                                                color: theme.palette.primary.main,
                                            }
                                        }}
                                    />
                                </ListItemIcon>
                            )}
                            {item.content ? (
                                item.content
                            ) : (
                                <ListItemText
                                    primary={
                                        <Typography
                                            sx={{
                                                fontWeight: item.checked ? 600 : 400,
                                                color: item.disabled ? theme.palette.text.disabled : theme.palette.text.primary,
                                            }}
                                        >
                                            {item.label}
                                        </Typography>
                                    }
                                    secondary={item.secondary}
                                />
                            )}
                        </Box>
                        {/* Actions on right side */}
                        {actions.length > 0 && (
                            useDropdown ? (
                                <>
                                    <IconButton
                                        size="small"
                                        onClick={e => handleMenuOpen(e, idx)}
                                    >
                                        <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                    <Menu
                                        anchorEl={menuAnchor}
                                        open={menuItemIdx === idx && Boolean(menuAnchor)}
                                        onClose={handleMenuClose}
                                        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                                        transformOrigin={{ vertical: "top", horizontal: "center" }}
                                    >
                                        {actions.map(action => (
                                            <MenuItem
                                                key={action.type}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    handleMenuClose();
                                                    action.onClick && action.onClick(item, idx);
                                                }}
                                            >
                                                <MuiListItemIcon>
                                                    {action.icon ?? getDefaultIcon(action.type)}
                                                </MuiListItemIcon>
                                                <MuiListItemText>{action.label ?? action.type}</MuiListItemText>
                                            </MenuItem>
                                        ))}
                                    </Menu>
                                </>
                            ) : (
                                <Box sx={{ ml: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                                    {actions.map(action => (
                                        <IconButton
                                            key={action.type}
                                            size="small"
                                            aria-label={action.type}
                                            onClick={e => {
                                                e.stopPropagation();
                                                action.onClick && action.onClick(item, idx);
                                            }}
                                        >
                                            {action.icon ?? getDefaultIcon(action.type)}
                                        </IconButton>
                                    ))}
                                </Box>
                            )
                        )}
                    </ListItem>
                );
            })}
        </List>
    );
};

export default DtList;